import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter } from 'rxjs';
import { CurrentContextService } from '../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthIndustryTopic,
  liveStatusTranslationKey
} from '../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../core/live-updates/live-update.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { InfoHintComponent } from '../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../shared/design-system/page.component';
import { GrowthPartyLabelService } from './growth-party-label.service';
import { GrowthAdminService, IndustryBindingResponse } from './growth-admin.service';
import { GROWTH_INDUSTRY_BINDING_ENTITY_TYPES } from './growth-industry-form.models';

@Component({
  standalone: true,
  selector: 'ain-growth-industry-bindings',
  imports: [AinPageComponent, InfoHintComponent, TableModule, FormsModule, ButtonModule, InputTextModule, SelectModule, RouterLink],
  template: `
    <ain-page [title]="t('growth.industryBindings.title')" [description]="t('growth.industryBindings.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.industryBindings.help.title'"
        [descriptionKey]="'growth.industryBindings.help.description'"
        [bulletKeys]="['growth.industryBindings.help.bullets.entity', 'growth.industryBindings.help.bullets.live']" />

      <section class="card list-card">
        <div class="toolbar">
          <input
            pInputText
            type="text"
            [(ngModel)]="searchTerm"
            [placeholder]="t('growth.industryBindings.search')" />
          <p-select
            [options]="entityTypeOptions()"
            [(ngModel)]="entityTypeFilter"
            optionLabel="label"
            optionValue="value"
            [showClear]="true"
            appendTo="body"
            [placeholder]="t('growth.industryBindings.filterEntityType')" />
          <button pButton type="button" [label]="t('growth.industryBindings.search')" (click)="applyFilters()"></button>
          <button pButton type="button" [label]="t('growth.industryBindings.refresh')" severity="secondary" (click)="load()"></button>
          <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
        </div>
        <div class="toolbar">
          <button pButton type="button" [label]="t('growth.industryBindings.addNew')" (click)="openCreate()"></button>
          <button
            pButton
            type="button"
            [label]="t('growth.industryBindings.openSelected')"
            severity="secondary"
            [disabled]="!selected()"
            (click)="openSelectedDetail()"></button>
          <a class="link-btn" routerLink="/growth/marketplace/installs">{{ t('growth.industryPacks.openList') }}</a>
        </div>
        <p-table
          [value]="filteredRows()"
          selectionMode="single"
          [selection]="selected()"
          (selectionChange)="onSelect($event)"
          dataKey="id">
          <ng-template pTemplate="header">
            <tr>
              <th>{{ t('growth.industryBindings.columns.industryKey') }}</th>
              <th>{{ t('growth.industryBindings.columns.entityType') }}</th>
              <th>{{ t('growth.industryBindings.columns.entityId') }}</th>
              <th>{{ t('growth.industryBindings.columns.partyId') }}</th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr [pSelectableRow]="row">
              <td>
                <button type="button" class="link-btn" (click)="openDetail(row.id)">{{ row.industryKey }}</button>
              </td>
              <td>{{ entityTypeLabel(row.entityType) }}</td>
              <td>{{ row.entityId }}</td>
              <td>{{ row.partyId ? partyLabels.label(row.partyId) : '-' }}</td>
            </tr>
          </ng-template>
        </p-table>
        @if (errorMessage()) {
          <p class="message error">{{ errorMessage() }}</p>
        }
      </section>
    </ain-page>
  `,
  styles: [`
    .card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 12px; background: #fff; }
    .list-card { display: grid; gap: 10px; }
    .toolbar { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
    .link-btn { border: 0; background: none; color: #1d4ed8; cursor: pointer; font-weight: 600; padding: 0; text-decoration: none; }
    .link-btn:hover { text-decoration: underline; }
    .message { margin: 0; font-size: 13px; }
    .message.error { color: #b91c1c; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
  `]
})
export class GrowthIndustryBindingsComponent implements OnInit {
  private readonly api = inject(GrowthAdminService);
  private readonly localization = inject(LocalizationService);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly partyLabels = inject(GrowthPartyLabelService);
  readonly liveStatusKey = liveStatusTranslationKey;

  readonly rows = signal<IndustryBindingResponse[]>([]);
  readonly filteredRows = signal<IndustryBindingResponse[]>([]);
  readonly selected = signal<IndustryBindingResponse | null>(null);
  readonly errorMessage = signal('');

  searchTerm = '';
  entityTypeFilter: string | null = null;

  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthIndustryChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load());
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    void this.setupLiveSubscription();
    this.load();
  }

  entityTypeOptions(): Array<{ label: string; value: string }> {
    return GROWTH_INDUSTRY_BINDING_ENTITY_TYPES.map(value => ({
      value,
      label: this.entityTypeLabel(value)
    }));
  }

  entityTypeLabel(value: string): string {
    return this.t(`growth.industryBindings.entityType.${value}`);
  }

  load(): void {
    this.errorMessage.set('');
    this.api.listBindings().subscribe({
      next: data => {
        this.partyLabels.resolve(data.filter(item => item.partyId).map(item => item.partyId!));
        this.rows.set(data);
        this.applyFilters();
      },
      error: () => {
        this.rows.set([]);
        this.filteredRows.set([]);
        this.selected.set(null);
        this.errorMessage.set(this.t('growth.industryBindings.messages.loadFailed'));
      }
    });
  }

  applyFilters(): void {
    const term = this.searchTerm.trim().toLowerCase();
    let filtered = this.rows();
    if (this.entityTypeFilter) {
      filtered = filtered.filter(item => item.entityType === this.entityTypeFilter);
    }
    if (term) {
      filtered = filtered.filter(item =>
        item.industryKey.toLowerCase().includes(term)
        || item.entityType.toLowerCase().includes(term)
        || item.entityId.toLowerCase().includes(term)
        || (item.partyId ? this.partyLabels.label(item.partyId).toLowerCase().includes(term) : false));
    }
    this.filteredRows.set(filtered);
    const currentId = this.selected()?.id;
    this.selected.set(filtered.find(item => item.id === currentId) ?? filtered[0] ?? null);
  }

  onSelect(row: IndustryBindingResponse | null): void {
    this.selected.set(row);
  }

  openCreate(): void {
    void this.router.navigate(['/growth/marketplace/bindings/new']);
  }

  openSelectedDetail(): void {
    const row = this.selected();
    if (!row) {
      return;
    }
    this.openDetail(row.id);
  }

  openDetail(bindingId: string): void {
    void this.router.navigate(['/growth/marketplace/bindings', bindingId]);
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthIndustryTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
